/**
 * @typedef {Object} variantData
 * @property {Object} variants
 * @property {string} formId
 * @property {string} metaData
 * @property {string} productUrl
 * @property {string} currentVariant
 */

/**
 * @typedef {Object.<string, variantData>} variants
 * @property {string} id
 * @property {string} sku
 * @property {boolean} available
 */

class VariantConfigurator {
    constructor(data) {
        this.metaFieldLabels = {
            'scope': 'Anwendungsbereich',
            'flow_qp': 'Durchfluss Qₚ m³/h',
            'flow_q3': 'Durchfluss Q3 m³/h',
            'dn_counter_dimension': 'DN Zählerdimension',
            'connection_counter': 'Anschluss in Zoll',
            'connection_pipe_side': 'Anschluss rohrseitig in Zoll',
            'meter_length_mm': 'Baulänge in mm',
            'sensor_diameter': 'Fühler Ø',
            'pn_bar': 'PN in Bar',
            'rosette': 'Rosette',
            'installation_position': 'Einbaulage',
            'hydromess': 'Hydromess',
            'up_housing': 'UP-Gehäuse',
            'implementation': 'Ausführung',
            'type': 'Typ',
            'counter': 'Für Zähler',
            'fitting': 'Armatur',
            'box_b_h_t_mm': 'Kasten B x H x T in mm',
            'material': 'Material',
            'nominal_size': 'Nenngröße',
            'ventile': 'Ventil'
        };

        this.productVariantData = data;

        this.organizedData = {};
        this.variantLookup = {};
        this.currentSelection = {};
        this.previousSelection = {};
        this.shopifyVariants = {};
        this.recentSelections = [];

        this.init();
    }

    init() {
        if (!this.productVariantData) {
            console.error("Variant Configurator could not find any ProductVariantData.");
            return;
        }

        this.shopifyVariants = this.productVariantData.variants;
        this.formId = this.productVariantData.formId;
        this.processMetaData();
        this.setInitialSelection();
        this.renderFilters();
        this.listenToShopifyVariantChange();
    }

    processMetaData() {
        const allMetaData = this.productVariantData.metaData.split('||').filter(item => item);

        allMetaData.forEach((entry) => {
            const [variantId, ...metaPairs] = entry.split(':');
            const metaString = metaPairs.join(':');
            const variantConfig = {};

            metaString.split(';').forEach(pair => {
                if (pair) {
                    const [key, rawvalue] = pair.split('=');
                    const cleanValue = String(rawvalue).trim();
                    const displayLabel = this.metaFieldLabels[key] || key;
                    variantConfig[key] = cleanValue;

                    if (!this.organizedData[displayLabel]) {
                        this.organizedData[displayLabel] = {
                            key,
                            values: {}
                        };
                    }

                    if (!this.organizedData[displayLabel].values[cleanValue]) {
                        this.organizedData[displayLabel].values[cleanValue] = [];
                    }

                    this.organizedData[displayLabel].values[cleanValue].push(variantId);
                }
            });
            if (!this.variantLookup[variantId]) {
                this.variantLookup[variantId] = {};
            }

            Object.assign(this.variantLookup[variantId], variantConfig);
        });
    }

    getCurrentVariantId() {
        if (this.productVariantData.currentVariant) {
            return this.productVariantData.currentVariant.toString();
        }

        const urlParams = new URLSearchParams(window.location.search);
        const variantFromUrl = urlParams.get('variant');
        if (variantFromUrl && this.shopifyVariants[variantFromUrl]) {
            return variantFromUrl;
        }

        const firstAvailable = Object.values(this.shopifyVariants).find(v => v.available);
        return firstAvailable ? firstAvailable.id.toString() : Object.keys(this.shopifyVariants)[0];
    }

    setInitialSelection() {
        const currentVariantId = this.getCurrentVariantId();

        if (currentVariantId && this.variantLookup[currentVariantId]) {
            const variantConfig = this.variantLookup[currentVariantId];
            this.currentSelection = {...variantConfig};
        }
    }

    renderFilters() {
        const container = document.getElementById('molline-variant-picker');
        if(!container) return;

        container.innerHTML = '';

        // sort data by this.metaFieldLabels order
        const sortedData = Object.values(this.metaFieldLabels).reduce((acc, labelName) => {
            if (this.organizedData[labelName]) {
                acc[labelName] = this.organizedData[labelName];
            }
            return acc;
        }, {});


        Object.keys(sortedData).forEach(filterLabel => {
            const { key: filterKey, values } = this.organizedData[filterLabel];
            const filterDiv = document.createElement('div');

            filterDiv.className = 'filter-group';
            filterDiv.innerHTML = `
                <h4>${filterLabel}</h4>
                <div class="filter-options" data-filter="${filterKey}">
                ${Object.keys(values).sort(this.sortValues).map(value => `
                    <button 
                      class="filter-option" 
                      data-filter="${filterKey}" 
                      data-value="${value}"
                    >
                      ${value}
                    </button>
                  `).join('')}
                </div>
            `;

            container.appendChild(filterDiv);

            const buttons = filterDiv.querySelectorAll('.filter-option');
            buttons.forEach(btn => {
                btn.addEventListener('click', (e) => {
                    this.selectMetaOption(
                        btn.dataset.filter,
                        btn.dataset.value
                    );
                });
            });
        });

        this.updateVisualState();
    }

    selectMetaOption(filterLabel, value) {
        this.currentSelection[filterLabel] = value;
        
        this.recentSelections = this.recentSelections.filter(item => item !== filterLabel);
        this.recentSelections.unshift(filterLabel);
        if (this.recentSelections.length > 2) {
            this.recentSelections = this.recentSelections.slice(0, 2);
        }
        
        this.updateVisualState();

        const matchingVariant = this.findBestMatchingVariant(filterLabel, value);

        if (matchingVariant) {
            this.selectShopifyVariant(matchingVariant.id);
        }
    }

    findBestMatchingVariant(optionLabel) {
        let availableVariants = Object.keys(this.variantLookup).filter(variantId => {
            const variantConfig = this.variantLookup[variantId];

            return Object.keys(this.currentSelection).every(selectedFilter => {
                return variantConfig[selectedFilter] === this.currentSelection[selectedFilter];
            });
        }).map(id => this.shopifyVariants[id]).filter(variant => variant.available);

        if (availableVariants.length === 0) {
            availableVariants = Object.keys(this.variantLookup).filter(variantId => {
                const variantConfig = this.variantLookup[variantId];
                return variantConfig[optionLabel] === this.currentSelection[optionLabel]
            }).map(id => this.shopifyVariants[id]).filter(variant => variant.available);
        }

        if (availableVariants.length <= 1) {
            return availableVariants[0] || null;
        }

        const bestVariant = availableVariants.reduce((best, current) => {
            const currentConfig = this.variantLookup[current.id];
            const bestConfig = this.variantLookup[best.id];
            
            let currentScore = 0;
            let bestScore = 0;
            
            Object.keys(this.currentSelection).forEach(key => {
                if (currentConfig[key] === this.currentSelection[key]) {
                    let weight = 1;
                    if (this.recentSelections[0] === key) weight = 3;
                    else if (this.recentSelections[1] === key) weight = 2;
                    currentScore += weight;
                }
                if (bestConfig[key] === this.currentSelection[key]) {
                    let weight = 1;
                    if (this.recentSelections[0] === key) weight = 3;
                    else if (this.recentSelections[1] === key) weight = 2;
                    bestScore += weight;
                }
            });
            
            return currentScore > bestScore ? current : best;
        });

        return bestVariant;
    }

    selectShopifyVariant(variantId) {
        const variantSelect = document.querySelector('[name="id"]');
        if (variantSelect) {
            variantSelect.value = variantId;
            const changeEvent = new Event('change', {bubbles: true});
            variantSelect.dispatchEvent(changeEvent);
        }

        void this.updateProductSection(variantId);
    }

    async updateProductSection(variantId) {
        try {
            const response = await fetch(`${this.productVariantData.productUrl}?variant=${variantId}`);
            if(response.ok) {
                const html = await response.text();
                this.updateProductInfo(html, variantId);
            }
        } catch (error) {
            console.error('Error updating product:', error);
        }
    }

    updateProductInfo(html, variantId) {
        const variant = this.shopifyVariants[variantId].shopifyVariant;
        const previousVariantId = new URLSearchParams(window.location.search).get('variant');
        const previousVariant = previousVariantId ? this.shopifyVariants[previousVariantId].shopifyVariant : null;
        const form = document.querySelector(`#${this.formId}`);
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(html, 'text/html');
        const productChange = false;

        const newUrl = `${this.productVariantData.productUrl}?variant=${variantId}`;
        window.history.replaceState({variantId}, '', newUrl);

        this.updateVisualState();

        form.dispatchEvent(new Event('change', {bubbles: true}));
        form.dispatchEvent(new CustomEvent('product:rerender', {
            detail: {
                htmlFragment: newDoc,
                productChange
            }
        }));

        if (!productChange){
            form.dispatchEvent(new CustomEvent('variant:change', {
                bubbles: true,
                detail: {
                    formId: form.id,
                    variant: variant,
                    previousVariant
                }
            }));
        }
    }

    updateVisualState() {
        Object.keys(this.organizedData).forEach(filterLabel => {
            const filterKey = this.organizedData[filterLabel].key;
            const values = this.organizedData[filterLabel].values;

            const filterContainer = document.querySelector(`[data-filter="${filterKey}"]`);

            if (filterContainer) {
                Object.keys(values).forEach(value => {

                    const button = filterContainer.querySelector(`[data-value="${value}"]`);
                    if (button) {
                        const isSelected = this.currentSelection[filterKey] === value;
                        const isCombinable = Object.keys(this.variantLookup).some(variantId => {
                            const variant = this.variantLookup[variantId];
                            
                            // Get all possible property keys from all variants
                            const allPossibleKeys = new Set();
                            Object.values(this.variantLookup).forEach(v => {
                                Object.keys(v).forEach(k => allPossibleKeys.add(k));
                            });

                            return Array.from(allPossibleKeys).every(propertyKey => {
                                const selectedValue = propertyKey === filterKey
                                    ? value
                                    : this.currentSelection[propertyKey];
                                
                                const variantValue = variant[propertyKey];
                                
                                // Both must be either undefined or have the same value
                                return (selectedValue === undefined && variantValue === undefined) ||
                                       (selectedValue !== undefined && variantValue !== undefined && selectedValue === variantValue);
                            });
                        });

                        button.classList.toggle('selected', isSelected);

                        if (!button.classList.contains('selected')) {
                            button.classList.toggle('combinable', isCombinable);
                        }
                    }
                });
            }
        });
    }

    listenToShopifyVariantChange() {
        const variantSelect = document.querySelector('[name="id"]');
        if (variantSelect) {
            variantSelect.addEventListener('change', (e) => {
                const newVariantId = e.target.value;
                if(this.variantLookup[newVariantId]) {
                    this.currentSelection = {...this.variantLookup[newVariantId]};
                    this.updateVisualState();
                }
            });
        }
    }

    sortValues(a, b) {
        return a - b;
    }
}

// Initialisierung
document.addEventListener('DOMContentLoaded', () => {
    const variantData = window.ProductVariantData || null;
    new VariantConfigurator(variantData);
});
